/**
 * The client's logo, next to its name wherever it is cited (Pascal,
 * 28/09/2026 : « trouver le logo de tous les clients et les afficher, chaque
 * fois que le nom du client est cité »). The server resolves the client's
 * web domain and gives a same-origin `client_logo_url`
 * (`/api/client-logo?domain=…`); without one, or when the image fails to
 * load, the client keeps its initials on a stable tint — never a broken
 * image, never an empty square.
 */
import { useState, type ReactNode } from 'react';

export function clientInitials(name: string): string {
  const parts = name.trim().split(/[\s\-–—'’]+/).filter((part) => /[\p{L}\p{N}]/u.test(part));
  if (!parts.length) return '?';
  const letters = parts.length === 1 ? parts[0].slice(0, 2) : `${parts[0][0]}${parts[1][0]}`;
  return letters.toUpperCase();
}

/** Stable avatar tint (0‥5) derived from the name, so a client keeps its colour. */
export function clientTint(name: string): number {
  let hash = 0;
  for (const char of name) hash = (hash * 31 + char.codePointAt(0)!) % 9973;
  return hash % 6;
}

// The server's logo route, or a verified file of the shipped catalogue.
const LOGO_ROUTE = /^(?:\/api\/client-logo\?domain=[a-z0-9.-]+|\/client-logos\/[a-z0-9-]+\.(?:png|jpg|webp))$/;

/** True for the server's logo route with a plain domain, nothing else. */
export function isLogoRoute(url: string): boolean {
  return LOGO_ROUTE.test(url);
}

/** Same-origin logo URL of a row, '' when the server gave none. */
export function clientLogoSrc(row: { client_logo_url?: unknown } | null | undefined): string {
  const value = row?.client_logo_url;
  if (typeof value !== 'string') return '';
  const url = value.trim();
  // Only the server's own logo route: anything else (a remote URL, a path
  // that a browser would read as another host, another route) is ignored.
  return isLogoRoute(url) ? url : '';
}

export type ClientLogoSize = 'sm' | 'md';

export function ClientLogo({
  name,
  src,
  size = 'sm',
  label = false,
  className = '',
}: {
  /** Client name: the initials fallback and, when `label`, the alternative text. */
  name: string;
  /** Same-origin logo URL (`client_logo_url`), '' or absent for the initials. */
  src?: string;
  /** sm = 20 px (list rows), md = 24 px (panels, client list). */
  size?: ClientLogoSize;
  /**
   * The logo is decorative next to a visible name (default): empty alt,
   * hidden from assistive tech. `label` names it when it stands alone.
   */
  label?: boolean;
  className?: string;
}) {
  const [failedSrc, setFailedSrc] = useState('');
  const url = src && isLogoRoute(src) ? src : '';
  const classes = `c-client-logo c-client-logo--${size}${className ? ` ${className}` : ''}`;
  if (url && failedSrc !== url) {
    return (
      <span className={`${classes} c-client-logo--image`} aria-hidden={label ? undefined : true} data-client-logo="image">
        <img
          src={url}
          alt={label ? name : ''}
          loading="lazy"
          decoding="async"
          draggable={false}
          onError={() => setFailedSrc(url)}
        />
      </span>
    );
  }
  return (
    <span
      className={`${classes} c-client-logo--tint-${clientTint(name)}`}
      aria-hidden={label ? undefined : true}
      role={label ? 'img' : undefined}
      aria-label={label ? name : undefined}
      data-client-logo="initials"
    >
      {clientInitials(name)}
    </span>
  );
}

/**
 * A client name preceded by its logo. The text keeps its ellipsis in dense
 * rows (`wrap` lets a panel title wrap). Without a name nothing is drawn:
 * a missing client never gets a logo of its own.
 */
export function ClientName({
  name,
  src,
  size = 'sm',
  wrap = false,
  className = '',
  textClassName = '',
  overflowTarget = false,
  children,
}: {
  name: string;
  src?: string;
  size?: ClientLogoSize;
  wrap?: boolean;
  className?: string;
  textClassName?: string;
  /** Displayed text when it differs from `name` (a link label, a highlight). */
  children?: ReactNode;
  /** Inside an `OverflowTooltip`: the name is what may be cut, so it is what the tooltip measures. */
  overflowTarget?: boolean;
}) {
  const text = children ?? name;
  if (!name.trim()) return <>{text}</>;
  return (
    <span className={`c-client-name${wrap ? ' c-client-name--wrap' : ''}${className ? ` ${className}` : ''}`}>
      <ClientLogo name={name} src={src} size={size} />
      <span
        className={`c-client-name__text${textClassName ? ` ${textClassName}` : ''}`}
        data-overflow-tooltip-target={overflowTarget ? '' : undefined}
      >
        {text}
      </span>
    </span>
  );
}

/**
 * The client's logo left of a title that is not the client's own name (a
 * session, a calendar event, a mission): the logo is labelled with the
 * client's name, which the tooltip shows (Planification pattern, Pascal
 * 29/09/2026; calendar and Ma semaine, 30/09/2026). Without a name nothing
 * is drawn.
 */
export function ClientTitleLogo({
  name,
  src,
  size = 'sm',
  className = '',
}: {
  name: string;
  src?: string;
  size?: ClientLogoSize;
  className?: string;
}) {
  if (!name.trim()) return null;
  return (
    <span className={`c-client-title-logo${className ? ` ${className}` : ''}`} title={name} data-client-title-logo="">
      <ClientLogo name={name} src={src} size={size} label />
    </span>
  );
}
